import { FormDatePicker } from '@/src/components/Form/FormDatePicker'
import { FormInput } from '@/src/components/Form/FormInput'
import { FormSelect } from '@/src/components/Form/FormSelect'
import { FormTextarea } from '@/src/components/Form/FormTextarea'
import { CreateButton } from '@/src/components/buttons/CreateButton'
import { CompanyHolidayAdjustmentForm } from '@/src/features/procurement-and-markets/load-planning/load-planning-adjustments/CompanyHolidayAdjustmentForm'
import {
  LoadAdjustmentFormTypeEnum,
  type LoadPlanningAdjustmentFormEmptyable,
} from '@/src/features/procurement-and-markets/load-planning/load-planning-adjustments/LoadPlanningAdjustment.types'
import { LoadPlanningAdjustmentLocationSelect } from '@/src/features/procurement-and-markets/load-planning/load-planning-adjustments/LoadPlanningAdjustmentLocationSelect'
import { QuantityAdjustmentForm } from '@/src/features/procurement-and-markets/load-planning/load-planning-adjustments/QuantityAdjustmentForm'
import { ReferencePeriodAdjustmentForm } from '@/src/features/procurement-and-markets/load-planning/load-planning-adjustments/ReferencePeriodAdjustmentForm'
import { RenewableProductionAdjustmentForm } from '@/src/features/procurement-and-markets/load-planning/load-planning-adjustments/RenewableProductionAdjustmentForm'
import { appColors } from '@/src/theme'
import { FlexCol } from '@components'
import {
  ADJUSTMENT_TYPE_LABEL_MAP,
  loadAdjustmentTypeSelectOptions,
} from '@features/procurement-and-markets/load-planning/load-planning-adjustments/LoadPlanningAdjustment.utils'
import { endOfYear, startOfDay } from 'date-fns'
import type { FormikProps } from 'formik'
import { useTranslation } from 'react-i18next'

interface LoadPlanningAdjustmentFormProps {
  formik: FormikProps<LoadPlanningAdjustmentFormEmptyable>
  onCalculate: () => void
  year: number
  isEditing?: boolean
}

const LoadPlanningAdjustmentForm = ({ formik, onCalculate, year, isEditing }: LoadPlanningAdjustmentFormProps) => {
  const { t } = useTranslation()

  const handleDateRangeUpdate = (dates: [string, string]) => {
    const initialDate = dates[0] ? dates[0] : ''
    const finalDate = dates[1] ? dates[1] : ''
    formik.setFieldValue('timeframeStart', initialDate)
    formik.setFieldValue('timeframeEnd', finalDate)
  }

  return (
    <FlexCol gap='16px' width={'100%'} flexGrow={1} flexShrink={1} flexBasis={0} padding={0}>
      <LoadPlanningAdjustmentLocationSelect
        value={formik.values.locationId}
        onChange={value => formik.setFieldValue('locationId', value)}
        isDisabled={isEditing}
      />

      <FormInput
        isRequired={true}
        onChange={e => formik.setFieldValue('name', e.target.value)}
        name='supplier'
        label={t('components.procurement.forecasting.modal.name')}
        placeholder={t('components.procurement.forecasting.modal.name_placeholder')}
        value={formik.values.name}
        fontWeight={'86'}
        labelFontSize={'14px'}
        labelFontColor={appColors.secondaryForeground}
        paddingLeft={'10px'}
        labelFontWeight={'86'}
      />

      <FormSelect
        isRequired={true}
        name='adjustmentType'
        label={t('components.procurement.forecasting.modal.type')}
        options={loadAdjustmentTypeSelectOptions(ADJUSTMENT_TYPE_LABEL_MAP(t))}
        placeholder={t('components.procurement.forecasting.modal.type_placeholder')}
        value={formik.values.type}
        onChange={option => formik.setFieldValue('type', option)}
        labelProps={{
          fontWeight: 86,
          fontSize: '14px',
          color: appColors.secondaryForeground,
        }}
        isDisabled={isEditing}
      />

      <FormDatePicker
        pickerType={'day'}
        isRange={true}
        name='dateRange'
        label={t('components.procurement.forecasting.modal.period_label')}
        infoTooltip={t('components.procurement.forecasting.modal.period_tooltip')}
        value={[formik.values.timeframeStart, formik.values.timeframeEnd]}
        onChange={dates => {
          const initialDate = dates[0] ? dates[0].toISOString() : ''
          const finalDate = dates[1] ? dates[1].toISOString() : ''
          handleDateRangeUpdate([initialDate, finalDate])
        }}
        isRequired={true}
        labelProps={{
          fontWeight: 86,
          fontSize: '14px',
          color: appColors.secondaryForeground,
        }}
        minDate={startOfDay(new Date())}
        maxDate={endOfYear(new Date(year, 1, 1))}
      />

      {formik.values.type === LoadAdjustmentFormTypeEnum.COMPANY_HOLIDAYS && <CompanyHolidayAdjustmentForm formik={formik} />}
      {formik.values.type === LoadAdjustmentFormTypeEnum.QUANTITY_ADJUSTMENT && (
        <QuantityAdjustmentForm formik={formik} isEditing={isEditing} />
      )}
      {formik.values.type === LoadAdjustmentFormTypeEnum.REFERENCE_PERIOD && <ReferencePeriodAdjustmentForm formik={formik} />}
      {formik.values.type === LoadAdjustmentFormTypeEnum.RENEWABLE_PRODUCTION && <RenewableProductionAdjustmentForm formik={formik} />}
      <CreateButton
        iconLeft={<span></span>}
        variant='primary'
        isDisabled={!formik.isValid || !formik.dirty}
        onClick={onCalculate}
        data-action='loadplan-adjustmentmodal-calculate-action'
      >
        <span className='flex items-center gap-2'>{t('components.procurement.forecasting.modal.form.calculate_button_text')}</span>
      </CreateButton>

      <FormTextarea
        label={t('components.procurement.forecasting.modal.comment')}
        value={formik.values.comment}
        onChange={e => formik.setFieldValue('comment', e.target.value)}
        name={'comment'}
        placeholder={t('components.procurement.forecasting.modal.form.comment_placeholder')}
        fontWeight={86}
        fontSize='14px'
        minHeight={'60px'}
        height={'60px'}
        padding={'10px'}
        borderRadius={'8px'}
        minWidth={'100%'}
        color={appColors.secondaryForeground}
        labelProps={{
          fontWeight: 86,
          fontSize: '14px',
          color: appColors.secondaryForeground,
        }}
      />
    </FlexCol>
  )
}

export default LoadPlanningAdjustmentForm
